import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FormInputFieldComponent } from '../../components/form-input-field/form-input-field.component';
import { FormSelectFieldComponent } from '../../components/form-select-field/form-select-field.component';
import { FormTextareaFieldComponent } from '../../components/form-textarea-field/form-textarea-field.component';
import { FormSubmitBarComponent } from '../../components/form-submit-bar/form-submit-bar.component';
import { StandardFormComponent } from '../../components/standard-form/standard-form.component';
import { LoadingButtonComponent } from '../../components/loading-button/loading-button.component';
import { UnauthPageIntroComponent } from '../../components/unauth-page-intro/unauth-page-intro.component';
import { UnauthenticatedPageComponent } from '../../components/unauthenticated-page/unauthenticated-page.component';
import { PublicLeadSubmissionErrorDialogService } from '../../services/public-lead-submission-error-dialog.service';
import { PublicLeadSubmissionService } from '../../services/public-lead-submission.service';
import { RecaptchaService } from '../../services/recaptcha.service';
import { UnauthenticatedForwardNavService } from '../../services/unauthenticated-forward-nav.service';
import { PUBLIC_MARKETING_RUNTIME_CONFIG } from '../../tokens/public-marketing-runtime.token';
import {
  IonNote,
  ViewWillEnter,
} from '@ionic/angular/standalone';
import {
  PUBLIC_LEAD_MAX_EMAIL,
  PUBLIC_LEAD_MAX_MESSAGE,
  PUBLIC_LEAD_MAX_NAME,
  publicLeadEmailValidator,
  trimmedRequired,
} from '../about-project/public-lead-form.validators';
import { CONTACT_REASON_OPTIONS, type ContactReason } from '../contact-reason.constants';

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [
    UnauthenticatedPageComponent,
    UnauthPageIntroComponent,
    FormInputFieldComponent,
    FormSelectFieldComponent,
    FormTextareaFieldComponent,
    FormSubmitBarComponent,
    StandardFormComponent,
    LoadingButtonComponent,
    ReactiveFormsModule,
    IonNote,
  ],
  templateUrl: './contact.component.html',
  styleUrl: '../about-project/about-project.component.css',
})
export class ContactComponent implements ViewWillEnter {
  private readonly recaptcha = inject(RecaptchaService);
  private readonly submissions = inject(PublicLeadSubmissionService);
  private readonly submitErrorDialog = inject(PublicLeadSubmissionErrorDialogService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly marketing = inject(PUBLIC_MARKETING_RUNTIME_CONFIG);
  readonly unauthNav = inject(UnauthenticatedForwardNavService);

  readonly appName = this.marketing.appName;
  readonly contactEmail = this.marketing.contactEmail?.trim() ?? '';
  readonly pageTitle = 'Contact';

  readonly showInlineForm = !!(this.marketing.apiUrl?.trim() && this.recaptcha.isConfigured());

  readonly maxNameLen = PUBLIC_LEAD_MAX_NAME;
  readonly maxEmailLen = PUBLIC_LEAD_MAX_EMAIL;
  readonly maxMessageLen = PUBLIC_LEAD_MAX_MESSAGE;

  readonly contactReasonOptions = CONTACT_REASON_OPTIONS;
  readonly contactReasonSelectOptions = CONTACT_REASON_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));

  readonly contactForm = this.fb.nonNullable.group({
    reason: ['', [trimmedRequired]],
    name: ['', [trimmedRequired, Validators.maxLength(PUBLIC_LEAD_MAX_NAME)]],
    email: [
      '',
      [trimmedRequired, Validators.maxLength(PUBLIC_LEAD_MAX_EMAIL), publicLeadEmailValidator],
    ],
    message: ['', [trimmedRequired, Validators.maxLength(PUBLIC_LEAD_MAX_MESSAGE)]],
  });

  submitting = false;
  errorMessage = '';
  submitted = false;

  ionViewWillEnter(): void {
    this.submitting = false;
  }

  get contactMailtoHref(): string {
    if (!this.contactEmail) {
      return '';
    }
    const subject = encodeURIComponent(`CrowdCTRL ${this.appName}: enquiry`);
    return `mailto:${this.contactEmail}?subject=${subject}`;
  }

  fieldError(controlName: 'reason' | 'name' | 'email' | 'message'): string {
    if (!this.submitted) {
      return '';
    }
    const c = this.contactForm.get(controlName);
    if (!c?.errors) {
      return '';
    }
    const e = c.errors;
    if (e['required']) {
      if (controlName === 'reason') {
        return 'Select a reason for contact.';
      }
      if (controlName === 'name') {
        return 'Enter your name.';
      }
      if (controlName === 'email') {
        return 'Enter your email.';
      }
      return 'Enter a message.';
    }
    if (e['maxlength']) {
      const max = e['maxlength'].requiredLength as number;
      return `Must be at most ${max} characters.`;
    }
    if (e['email']) {
      return 'Enter a valid email address.';
    }
    return '';
  }

  async onSubmit(): Promise<void> {
    this.errorMessage = '';
    this.submitted = true;

    if (!this.showInlineForm) {
      return;
    }

    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      this.errorMessage = 'Please correct the highlighted fields.';
      return;
    }

    const { reason, name, email, message } = this.contactForm.getRawValue();
    const reasonT = reason.trim() as ContactReason;
    const nameT = name.trim();
    const emailT = email.trim().toLowerCase();
    const messageT = message.trim();

    this.submitting = true;
    try {
      const recaptchaToken = await this.recaptcha.execute('contact');
      await this.submissions.submit({
        kind: 'contact',
        recaptchaToken,
        reason: reasonT,
        name: nameT,
        email: emailT,
        organisation: '',
        message: messageT,
      });
      this.contactForm.reset(
        { reason: '', name: '', email: '', message: '' },
        { emitEvent: false },
      );
      this.submitted = false;
      const navigated = await this.router.navigateByUrl('/contact/sent', { replaceUrl: true });
      if (!navigated) {
        this.submitting = false;
        this.errorMessage = 'Could not open the confirmation page. Please try again.';
      }
    } catch (e) {
      this.submitting = false;
      this.errorMessage =
        e instanceof Error ? e.message : 'Something went wrong. Please try again.';
      await this.submitErrorDialog.show(e);
    }
  }
}
